import { Breadcrumb, Button, Dropdown, Tag } from "antd";
import React, { useEffect, useState } from "react";
import Table, { ColumnType } from "antd/es/table";

import { CloseOutlined, SlidersOutlined } from "@ant-design/icons";

import { cn } from "../../../lib/utils";
import MyPagination from "../../components/Pagination";
import { IBucketPayloadType } from "@src/types/api/filling-management";
import { IUser } from "../../../types/api/user";
import {
  useFIllingManagementList,
  useSendReminder,
} from "../../../hooks/api/filling-management/hooks";
import { FillingManagementFilterMenu } from "./FillingManagementFilter";
import { useMessageApi } from "../../../provider/AntdNotification";
import { useNavigate } from "react-router-dom";

const FIllingManagementTable = ({
  allowURLStateUpdate = true,
}: {
  allowURLStateUpdate?: boolean;
}) => {
    const navigate = useNavigate();

  const [breadcrumb] = useState("Filling");
  const messageApi = useMessageApi();
  const [query, setQuery] = useState<Record<string, any>>({
  page: 1,
  limit: 20,
});

  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useFIllingManagementList(query);

  /** ---- 🎯 Store filters as single-value only ---- **/
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string>
  >({});

  /** ---- Remove selected filter ---- **/
const removeFilter = (key: string) => {
  setSelectedFilters((prev) => {
    const updated = { ...prev };
    delete updated[key];

    setQuery((prevQuery) => ({
      ...prevQuery,
      page: 1,
      ...updated,
    }));

    return updated;
  });
};

  const { mutate: sendReminder } = useSendReminder({
    onSuccess: () => {
      messageApi.success("Reminder Sent SuccessFully");
    },

    onError: (err: any) => {
      messageApi.error(err.message || "Error in Sending Reminder");
    },
  });

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const qData = data?.qData ?? { page: 1, limit: 20 };

  const columns: ColumnType<IBucketPayloadType>[] = [
    {
      title: "User ID",
      dataIndex: "user",
      render: (user: IUser) => <span>{user?.user_Id || "-"}</span>,
    },
    {
      title: "Name",
      dataIndex: "user",
      render: (user: IUser) => <span>{user?.name || "-"}</span>,
    },
    {
      title: "Year",
      dataIndex: "filling_year",
      render: (year: string) => <span className="text-gray-800">{year || "-"}</span>,
    },
    {
      title: "Type",
      dataIndex: "filling_type",
      render: (t: string) => <span>{t || "-"}</span>,
    },
    {
      title: "Receipt No",
      dataIndex: "receipt_no",
      render: (v: string) => <span className="font-mono text-xs">{v || "-"}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (_, row) => {
        const status = row.status ?? "DRAFT";
        const colorMap: Record<string, string> = {
          DRAFT: "bg-blue-100 text-blue-700",
          SUBMITTED: "bg-green-100 text-green-700",
          PAID: "bg-purple-100 text-purple-700",
        };
        const labelMap: Record<string, string> = {
          DRAFT: "In Progress",
          SUBMITTED: "Submitted",
          PAID: "Paid",
        };
        return (
          <div className="flex flex-col gap-1">
            <Tag
              className={cn(
                "px-3 py-1 rounded-full text-xs font-semibold capitalize w-fit",
                colorMap[status],
              )}
            >
              {labelMap[status] ?? status}
            </Tag>
            {(row as any).formDetails?.crystallised && (
              <span className="text-[10px] text-green-600 font-medium">✓ Crystallised</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Progress",
      dataIndex: "progress",
      render: (p: number) => {
        const pct = p ?? 0;
        return (
          <div className="flex flex-col gap-1 min-w-[80px]">
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${pct}%`,
                  background: pct === 100 ? "#16a34a" : pct >= 50 ? "#f97316" : "#3b82f6",
                }}
              />
            </div>
            <span className="text-xs text-gray-500">{pct}% complete</span>
          </div>
        );
      },
    },
    {
      title: "Complexity",
      dataIndex: "complexity",
      render: (c: any) => {
        const level = c?.level || "LOW";
        const colorMap: Record<string, string> = { LOW: "cyan", MEDIUM: "orange", HIGH: "red" };
        const labelMap: Record<string, string> = {
          LOW: "Low (auto-file)",
          MEDIUM: "Medium (expert review)",
          HIGH: "High (contact)",
        };
        return <Tag color={colorMap[level]}>{labelMap[level] ?? level}</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, record) => (
        <div className="flex items-center justify-end gap-3">
          <span
            className="text-primary cursor-pointer underline text-sm"
            onClick={() => sendReminder({ userId: record?.user?._id || "" })}
          >
            Send Reminder
          </span>
          <span
            onClick={() => navigate(`/filling-management/${record._id}`)}
            className="cursor-pointer text-primary text-sm font-medium"
          >
            View
          </span>
        </div>
      ),
    },
  ];
  useEffect(() => {
  setQuery((prev) => ({
    ...prev,
    page: 1,
    ...selectedFilters,
  }));
}, [selectedFilters]);

useEffect(() => {
  refetch(query);
}, [query]);

  return (
    <div>
      <h1 className="text-lg sm:text-3xl font-semibold">FIlling Management </h1>

      <Breadcrumb
        separator=">"
        items={[{ title: "Dashboard" }, { title: breadcrumb }]}
        className="text-xs sm:text-base my-2"
      />

      {/* Filter Button */}
      <div className="flex justify-end mb-3">
        <Dropdown
          trigger={["click"]}
          placement="bottomRight"
          dropdownRender={() => (
            <FillingManagementFilterMenu
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
            />
          )}
        >
          <Button
            icon={<SlidersOutlined />}
            className="bg-[#FEF2E7] custom_shadow font-semibold"
          >
            Filters
          </Button>
        </Dropdown>
      </div>

      {/* Selected Filter Tags */}
      {Object.keys(selectedFilters).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(selectedFilters).map(([key, value]) => (
            <Tag
              key={key}
              closable
              color="green"
              onClose={() => removeFilter(key)}
              closeIcon={<CloseOutlined />}
            >
              {value}
            </Tag>
          ))}
        </div>
      )}

      {/* Table */}
      <Table
        dataSource={data?.data}
        columns={columns}
        rowKey={(item) => item._id ?? ""}
        loading={isFetching}
        pagination={false}
        scroll={{ x: true }}
        size="middle"
        className="custom-rounded-table"
        footer={() => (
          <MyPagination
            qData={qData}
            onChange={refetch}
            total={qData.total}
            DEFAULT_NUQS_CONFIG={DEFAULT_NUQS_CONFIG}
          />
        )}
      />
    </div>
  );
};

export default FIllingManagementTable;
